/**
 * Shared deal sort options for the board (Kanban) and list views, plus the
 * per-workspace memory of the last choice.
 *
 * Each option encodes a column + direction in a single dropdown value so
 * the UI stays a plain <select>. "Last activity: oldest first" is the
 * answer to "show me the leads I haven't contacted recently" — it surfaces
 * never-contacted deals (NULL last_activity) at the top.
 *
 * The chosen sort persists to `localStorage`, deliberately NOT to a
 * server-side setting. Fias Leads Holders can be ORG Holders shared by a
 * whole sales team, and every server-side preference store in the codebase
 * (`email_user_settings`, `fias360_workspace_preferences`) is keyed by
 * Holder alone — so persisting there would mean one rep changing their sort
 * silently re-sorts every teammate's board. Sort order is a personal lens on
 * shared data, so it stays on the device. The trade-off is that it does not
 * follow the user to another browser.
 */

import { useCallback, useState } from 'react';
import type { FiasLeadsDealSortBy } from '@/lib/api';

export interface DealSortOption {
  value: string;
  label: string;
  sortBy: FiasLeadsDealSortBy;
  sortDir: 'asc' | 'desc';
}

export const DEAL_SORT_OPTIONS: DealSortOption[] = [
  { value: 'updated_at:desc', label: 'Recently updated', sortBy: 'updated_at', sortDir: 'desc' },
  {
    value: 'last_activity_at:asc',
    label: 'Last activity: oldest first',
    sortBy: 'last_activity_at',
    sortDir: 'asc',
  },
  {
    value: 'last_activity_at:desc',
    label: 'Last activity: newest first',
    sortBy: 'last_activity_at',
    sortDir: 'desc',
  },
  { value: 'value:desc', label: 'Value: high → low', sortBy: 'value', sortDir: 'desc' },
  { value: 'value:asc', label: 'Value: low → high', sortBy: 'value', sortDir: 'asc' },
  {
    value: 'expected_close_date:asc',
    label: 'Close date: soonest',
    sortBy: 'expected_close_date',
    sortDir: 'asc',
  },
  { value: 'name:asc', label: 'Name: A → Z', sortBy: 'name', sortDir: 'asc' },
];

export const DEFAULT_DEAL_SORT = DEAL_SORT_OPTIONS[0];

export function parseDealSort(value: string): DealSortOption {
  return DEAL_SORT_OPTIONS.find((o) => o.value === value) ?? DEFAULT_DEAL_SORT;
}

/**
 * Versioned storage key. Bump the suffix if the stored shape ever grows
 * incompatible fields and orphan the old key — a stale few-hundred-byte
 * record costs nothing against a multi-MB quota.
 */
export const DEAL_SORT_STORAGE_KEY = 'fias-leads:deal-sort:v1';

/**
 * Cap on remembered workspaces. A user realistically has a personal Holder
 * plus a handful of org Holders, so this only ever trims pathological
 * cases; it exists so the entry can never grow without bound. Objects keep
 * string-key insertion order, so trimming from the front drops the
 * least-recently-written workspaces.
 */
const MAX_REMEMBERED_HOLDERS = 25;

/**
 * `localStorage` access that degrades to "no memory" instead of throwing.
 * Safari private mode rejects writes with a quota error and SSR has no
 * `window` — in both cases a forgotten sort order is a mild annoyance,
 * never a reason to break the page.
 */
function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Reads the persisted `{ holderId: sortValue }` map, dropping anything that
 * isn't a recognised sort option. The payload is mutable from devtools and
 * may have been written by an older release, so nothing is trusted: an
 * unparseable or foreign-shaped blob yields an empty map.
 */
function loadSortMap(storage: Storage | null = safeStorage()): Record<string, string> {
  if (!storage) return {};
  try {
    const raw = storage.getItem(DEAL_SORT_STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const known = new Set(DEAL_SORT_OPTIONS.map((o) => o.value));
    const clean: Record<string, string> = {};
    for (const [holderId, value] of Object.entries(parsed)) {
      if (typeof value === 'string' && known.has(value)) clean[holderId] = value;
    }
    return clean;
  } catch {
    return {};
  }
}

/**
 * The sort remembered for `holderId`, or the default when this workspace
 * has never been sorted (or storage is unavailable).
 */
export function loadDealSort(holderId: string, storage?: Storage | null): DealSortOption {
  const stored = loadSortMap(storage)[holderId];
  return stored ? parseDealSort(stored) : DEFAULT_DEAL_SORT;
}

/**
 * Remembers `value` as this workspace's sort. Re-writing an existing
 * holder deletes the key first so it moves to the end of the insertion
 * order and the trim below evicts genuinely idle workspaces rather than
 * whichever one happened to be written first.
 */
export function saveDealSort(holderId: string, value: string, storage?: Storage | null): void {
  const target = storage === undefined ? safeStorage() : storage;
  if (!target) return;
  const map = loadSortMap(target);
  delete map[holderId];
  map[holderId] = value;
  const keys = Object.keys(map);
  for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_REMEMBERED_HOLDERS))) {
    delete map[stale];
  }
  try {
    target.setItem(DEAL_SORT_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Quota exceeded / storage disabled — the sort just won't be remembered.
  }
}

/**
 * Deal-sort state for a workspace, seeded from the remembered choice and
 * written back on every change. Shared by the board and list views so the
 * two stay in agreement — picking a sort on the board and switching to the
 * list keeps it.
 *
 * `holderId` is read lazily by the initializer, so the first render already
 * has the persisted value; there is no default-then-correct flash.
 *
 * Switching workspace re-seeds from that workspace's own remembered sort.
 * The initializer alone can't do this — it runs once per mount, and the
 * deals pages stay mounted across a Holder switch — so the change is
 * applied during render (React's sanctioned "adjust state when a prop
 * changes" pattern, which re-renders immediately without committing the
 * stale value or paying for an effect round-trip).
 */
export function useDealSort(holderId: string): {
  sortValue: string;
  setSortValue: (next: string) => void;
  sort: DealSortOption;
} {
  const [sortValue, setSortValueState] = useState<string>(() => loadDealSort(holderId).value);
  const [seededFor, setSeededFor] = useState<string>(holderId);

  if (seededFor !== holderId) {
    setSeededFor(holderId);
    setSortValueState(loadDealSort(holderId).value);
  }

  const setSortValue = useCallback(
    (next: string) => {
      // Normalize before persisting so a stray value from a <select> that
      // has drifted out of sync can never poison the stored map.
      const resolved = parseDealSort(next).value;
      setSortValueState(resolved);
      saveDealSort(holderId, resolved);
    },
    [holderId],
  );

  return { sortValue, setSortValue, sort: parseDealSort(sortValue) };
}
